import { SwitchProps } from './props';
import styles from './styles.module.scss';

/**
 * Switch Component
 * A customizable switch/toggle component that can be used for boolean input with optional label.
 * Supports loading states, disabled state, and flexible positioning.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.label] - Optional label text to display next to switch
 * @param {boolean} [props.disabled] - Whether the switch is disabled
 * @param {boolean} props.isChecked - Current checked state of the switch
 * @param {() => void} [props.onChange] - Callback function when switch state changes
 * @param {boolean} [props.isLoading] - Whether the switch is in loading state
 * @param {boolean} [props.flexEnd] - Whether to align switch to the end of container
 * @returns {JSX.Element} The rendered switch component
 *
 * @example
 * <Switch
 *   label="Enable notifications"
 *   disabled={false}
 *   isChecked={notificationsEnabled}
 *   onChange={() => setNotificationsEnabled(!notificationsEnabled)}
 *   isLoading={false}
 *   flexEnd={true}
 * />
 */
export function Switch({
  label,
  disabled,
  isChecked,
  onChange,
  isLoading,
  flexEnd,
}: SwitchProps) {
  /**
   * Handles the switch state change
   * Prevents default behavior and event propagation
   * Calls onChange callback if provided
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - Change event object
   */
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    e.stopPropagation();

    if (onChange) {
      onChange();
    }
  }

  // Show loading skeleton if in loading state
  if (isLoading) {
    return <div className={styles.skeleton} />;
  }

  return (
    <label
      className={`${styles.container} ${flexEnd ? styles['container--end'] : ''}`}
    >
      <div
        className={`${styles.switch} ${isChecked ? styles['switch--checked'] : ''} ${
          isLoading || disabled ? styles['switch--disabled'] : ''
        }`}
      >
        <div className={styles.switch__button}>
          {isLoading && (
            <i className={`${styles.spinner} fas fa-circle-notch fa-spin`} />
          )}
        </div>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={handleChange}
          className={styles.switch__input}
        />
      </div>

      {label &&
        (isLoading ? (
          <div className={styles['label-skeleton']} />
        ) : (
          <span className={styles.container__label}>{label}</span>
        ))}
    </label>
  );
}
